import { useInterval } from "ahooks";
import {
    duration,
    isExperienceProduct,
    isGiftOfferProduct,
    isMembershipProduct,
    isMerchandiseProduct,
    isPackageProduct,
    isPartnerPackageProduct,
} from "common";
import { camelCase } from "lodash";
import pluralize from "pluralize";
import { useEffect, useRef, useState } from "react";
import {
    Experience,
    GiftOffer,
    Membership,
    Merchandise,
    Package,
    PartnerPackage,
    Product,
    ProductObject,
    Purchase,
    PurchaseItemObject,
} from "types";
import { populateCollection, populateResource } from "../helper";
import { validateParams } from "../hookHelpers";
import { useAutoFetchCollection } from "../useAutoFetchCollection";
import { ResourceFetchOptions, useResource } from "../useResource";
import { queryClient } from "@/api/queryClient";
import { useUser } from "@/hooks/useAccount";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { isAffiliate } from "@/models/User";

export const useProduct = <T extends Product>(
    productId: string | undefined,
    params: any = null,
    options: ResourceFetchOptions<T> = {},
) => {
    params = validateParams(params);

    const response = useResource<T>(["products", productId], params, options);
    return { ...response, product: response.data, isLoadingProduct: response.isLoading };
};

export const getProductFromCache = <T extends Product>(productId: string): T | undefined => {
    return queryClient.getQueryData<T>(["products", productId]);
};

export const useExperience = (
    experienceId: string | undefined,
    params: any = null,
    options: ResourceFetchOptions<Experience> = {},
) => {
    params = validateParams(params);

    const response = useResource<Experience>(["experiences", experienceId], params, options);
    return { ...response, experience: response.data, isLoadingExperience: response.isLoading };
};

interface ProductsState {
    experiences: Experience[];
    memberships: Membership[];
    giftOffers: GiftOffer[];
    packages: Package[];
    partnerPackages: PartnerPackage[];
    merchandises: Merchandise[];
}

const expandParam = {
    expand: "selectedProducts,purchaseRule",
};

export const useProducts = (shouldWait = false) => {
    const { pathname } = window.location;
    const user = useUser();
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<ProductsState>({
        experiences: [],
        memberships: [],
        giftOffers: [],
        packages: [],
        partnerPackages: [],
        merchandises: [],
    });

    const pathRef = useRef("");
    const params = isAffiliate(user) ? { agent: true, ...expandParam } : expandParam;

    const query = useAutoFetchCollection<Product>("products", params, {
        keepPreviousData: true,
        enabled: !shouldWait,
        onSuccess: (data) => {
            const products = data?.pages.flatMap((page) => page.data);
            pathRef.current = pathname;
            for (const product of products) {
                if (product?.id) {
                    // Convert 'experience' to experiences, 'gift_offer' to 'giftOffers' etc
                    const collectionName = camelCase(pluralize(product.object ?? ProductObject.EXPERIENCE));
                    populateResource(collectionName, product);
                }
            }
        },
    });

    const { data, isLoading, isSuccess, hasMore, isFetchingNextPage, isLoadingPage, dataUpdatedAt, fetchNextPage } =
        query;

    const intervalCheck = hasMore && isFetchingNextPage && isLoadingPage;
    const delayMs = intervalCheck ? duration(5, "seconds") : undefined;

    useInterval(() => {
        const currentTime = Date.now();
        // We are checking for the path here because initially this logic was needed to handle case for sellers with 1k+ products,
        // when switching pages query would get stuck and half products will be missing,
        // but we don't need to trigger the fetch again if there was no redirect and query itself takes too long.
        if (pathRef.current !== pathname && isFetchingNextPage && delayMs && currentTime - dataUpdatedAt > delayMs) {
            fetchNextPage();
        }
    }, delayMs);

    useEffect(() => {
        if (isSuccess && !isLoading) {
            const experiences = data.filter((product) => isExperienceProduct(product)) as Experience[];
            const memberships = data.filter((product) => isMembershipProduct(product)) as Membership[];
            const giftOffers = data.filter((product) => isGiftOfferProduct(product)) as GiftOffer[];
            const packages = data.filter((product) => isPackageProduct(product)) as Package[];
            const partnerPackages = data.filter((product) => isPartnerPackageProduct(product)) as PartnerPackage[];
            const merchandises = data.filter((product) => isMerchandiseProduct(product)) as Merchandise[];

            setProducts(data);
            setFilteredProducts({ experiences, memberships, giftOffers, packages, partnerPackages, merchandises });
            populateCollection("experiences", experiences);
            populateCollection("memberships", memberships);
            populateCollection("giftOffers", giftOffers);
            populateCollection("packages", packages);
            populateCollection("partnerPackages", partnerPackages);
            populateCollection(MERCHANDISE_CACHE_KEY, merchandises);
        }
    }, [isSuccess, isLoading, data]);

    // Return 'products' 'filteredProducts' 'experiences' 'giftOffers 'packages' in addition to the query
    return { products, ...filteredProducts, filteredProducts, ...query };
};

export const useMerchandise = (params: any = null, options = {}) => {
    const [merchandise, setMerchandise] = useState<Merchandise[]>([]);
    const query = useAutoFetchCollection<Merchandise>(MERCHANDISE_API_PATH, params, options);

    const { data, isLoading, isSuccess } = query;

    useEffect(() => {
        if (isSuccess && !isLoading) {
            setMerchandise(data);
            for (const merchandise of data) {
                if (merchandise?.id) {
                    populateResource("products", merchandise);
                }
            }
        }
    }, [isSuccess, isLoading, data]);

    return { merchandise, ...query };
};

export const useExperiences = (params: any = null, options = {}) => {
    const [experiences, setExperiences] = useState<Experience[]>([]);
    const query = useAutoFetchCollection<Experience>("experiences", params, options);

    const { data, isLoading, isSuccess } = query;

    useEffect(() => {
        if (isSuccess && !isLoading) {
            setExperiences(data);
            for (const experience of data) {
                if (experience?.id) {
                    populateResource("products", experience);
                }
            }
        }
    }, [isSuccess, isLoading, data]);

    return { experiences, ...query };
};

export const usePartnerExperiences = (options = {}) => {
    // we want partner experiences to load into products as well
    return useExperiences({ partnerExperiences: true, expand: "seller.name" }, options);
};

export const usePurchaseExperiences = (purchase: Purchase) => {
    return purchase.items
        .filter((item) => item.object === PurchaseItemObject.EXPERIENCE)
        .map((item) => queryClient.getQueryData<Experience>(["products", item.product.id]))
        .filter(Boolean) as Experience[];
};
